import React, { useCallback, useMemo, useState } from "react";
import EntityList from "../EntityList/EntityList";
import styled from "styled-components";
import moment from "moment";
import swal from "sweetalert";
import { defineMessages, injectIntl } from "react-intl";
import {
  CONTROLS_POSITION,
  ENTITY_NAME_TO_SECTION,
  LOCALE,
  RATE_TYPE,
  RESTRICTION_CONFIGURATION_TYPE,
  RESTRICTION_TYPE,
  RESTRICTION_VALUE_TYPE,
  SHARING_TYPE,
} from "../../Common/constants";
import { bookableEntityToListValuesMapper } from "../Bookable/ModuleDefinition";
import { transformEntity } from "./utils";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import { customEntityToFormValuesMapper } from "./PromotionsForm.jsx";
import globalMessages from "../../intl/globalMessages";
import { useChainData } from "../../../../components/chain-data-provider";
import { usePermissions } from "../../../../lib/permissions";
import { ForbiddenSection } from "../../../../components/authorization-verifier";
import actions from "../../../../lib/actions";
import { useAuthorizer } from "../../../../lib/action-authorizer";
import axios from "../../../../http/customAxios";
import { createExcelFileDownloadFromRawData } from "../../../../lib/supportFunctions";

const messages = defineMessages({
  ...globalMessages,
  confirmation: {
    id: "app.promotionsList.confirmation",
    description: "Are you sure?",
    defaultMessage: "Are you sure?",
  },
  confirmationIsChain1: {
    id: "app.promotionsList.confirmationIsChain1",
    description: "The promo",
    defaultMessage: "The promo",
  },
  confirmationIsChain2: {
    id: "app.promotionsList.confirmationIsChain2",
    description: "will be deleted from all associated booking engines",
    defaultMessage: "will be deleted from all associated booking engines",
  },
  confirmDelete: {
    id: "app.promotionsList.confirmDelete",
    description: "Confirm delete",
    defaultMessage: "Yes, delete",
  },
  deleteConfirmationWarning: {
    id: "app.entityList.deleteConfirmationWarning",
    description: "Confirmation Warning",
    defaultMessage: "Once deleted, you will not be able to recover this entity!",
  },
  deleteSuccess: {
    id: "app.entityList.deleteSuccess",
    description: "Delete Success message",
    defaultMessage: "Entity successfully deleted!",
  },
  deleteError: {
    id: "app.entityList.deleteError",
    description: "Delete Error message",
    defaultMessage: "Error deleting entity",
  },
  deleteOk: {
    id: "default.ok",
    defaultMessage: "OK",
  },
  deleteTooltip: {
    id: "app.entityList.tooltip.delete",
    description: "Delete message",
    defaultMessage: "Delete",
  },
});

const Container = styled.div`
  .ReactVirtualized__Table__rowColumn:last-child {
    flex: 1 1 135px !important;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    flex: 1 1 135px !important;
  }
`;

function getConfigurationTypeText(restriction, ticker) {
  let result = "";

  if (restriction) {
    switch (restriction.configurationType) {
      case RESTRICTION_CONFIGURATION_TYPE.CONSTANT:
        if (restriction.restrictionValue && restriction.restrictionValue.value) {
          if (restriction.restrictionValue.value.length > 1) {
            result = "Múltiple";
          } else if (restriction.restrictionValue.value[0]) {
            const dateFormat = "DD/MM/YYYY";
            result = `${moment(restriction.restrictionValue.value[0].startDate).format(
              dateFormat
            )}-${moment(restriction.restrictionValue.value[0].endDate).format(dateFormat)}`;
          }
        }

        break;

      case RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT:
        result = "";
        break;

      case RESTRICTION_CONFIGURATION_TYPE.OWN:
        result = ticker;
        break;

      case RESTRICTION_CONFIGURATION_TYPE.SHARED:
        result = "Compartida";
        break;

      default:
        break;
    }
  }

  return result;
}

function getContractableConditionValue(restriction) {
  let result = "";

  if (restriction) {
    switch (restriction.configurationType) {
      case RESTRICTION_CONFIGURATION_TYPE.CONSTANT:
        result =
          restriction.restrictionValue &&
          restriction.restrictionValue.value &&
          restriction.restrictionValue.value.duration;
        break;

      case RESTRICTION_CONFIGURATION_TYPE.NO_LIMIT:
        result = "Sin límite";
        break;

      case RESTRICTION_CONFIGURATION_TYPE.OWN:
        result = "Propia";
        break;

      case RESTRICTION_CONFIGURATION_TYPE.SHARED:
        result = "Compartida";
        break;
      default:
        break;
    }
  }

  return result;
}

function calculatePromotionType(entity) {
  const rateValue = entity.rate?.rateValue?.value;
  if (rateValue === 0) {
    return RATE_TYPE.HIGHLIGHT_MESSAGE;
  } else if (entity.discountPeriodicity) {
    return RATE_TYPE.GIVE_NIGHT;
  } else if (entity.rate?.rateValue?.valueType === RESTRICTION_VALUE_TYPE.MONEY) {
    return RESTRICTION_VALUE_TYPE.MONEY;
  }
  return RATE_TYPE.PERCENTAGE;
}

function customEntityToListValuesMapper({ entities, currentLocale }) {
  return entities.map(oldEntity => {
    // TODO: don't do this shit
    const entity = JSON.parse(JSON.stringify(oldEntity));
    entity.type =
      entity.sharingType === SHARING_TYPE.SHARED
        ? currentLocale === LOCALE.es
          ? "Compartida"
          : "Shared"
        : " ";

    entity.rateType = entity.rate.rateType;
    entity.promotionType = calculatePromotionType(entity);
    switch (entity.rateType) {
      case RESTRICTION_CONFIGURATION_TYPE.OWN:
        entity.rate = "Propio";
        break;

      case RESTRICTION_CONFIGURATION_TYPE.SHARED:
        entity.rate = "Compartido";
        break;

      default:
        if (entity.rate.rateValue) {
          entity.rate =
            entity.rateType === RATE_TYPE.PERCENTAGE
              ? `-${entity.rate.rateValue.value}%`
              : `${entity.rate.rateValue.value.amount ? "-" : ""}${
                  entity.rate.rateValue.value.amount
                }`;
        }
        break;
    }

    if (entity.restrictions) {
      Object.keys(entity.restrictions).forEach(restriction => {
        switch (restriction) {
          case RESTRICTION_TYPE.OPEN_TRAVEL:
          case RESTRICTION_TYPE.OPEN_BOOKING:
          case RESTRICTION_TYPE.CLOSE_BOOKING:
            entity[`${restriction}_value`] = getConfigurationTypeText(
              entity.restrictions[restriction],
              entity.ticker
            );
            entity[restriction] = entity.restrictions[restriction].configurationType;

            break;
          case RESTRICTION_TYPE.CLOSE_TRAVEL:
            entity[`${restriction}_value`] =
              getConfigurationTypeText(entity.restrictions[restriction], entity.ticker) || "--";
            entity[restriction] = entity.restrictions[restriction].configurationType;
            break;
          case RESTRICTION_TYPE.MIN_STAY:
          case RESTRICTION_TYPE.MAX_STAY:
          case RESTRICTION_TYPE.MIN_NOTICE:
          case RESTRICTION_TYPE.MAX_NOTICE:
            entity[`${restriction}_duration`] = getContractableConditionValue(
              entity.restrictions[restriction]
            );
            entity[restriction] = entity.restrictions[restriction].configurationType;

            break;

          case RESTRICTION_TYPE.CODE:
            entity[restriction] =
              entity.restrictions[restriction] &&
              entity.restrictions[restriction].restrictionValue &&
              entity.restrictions[restriction].restrictionValue.value &&
              entity.restrictions[restriction].restrictionValue.value.join(", ");
            break;

          default:
            entity[restriction] =
              entity.restrictions[restriction] &&
              entity.restrictions[restriction].restrictionValue &&
              entity.restrictions[restriction].restrictionValue.value;
            break;
        }

        entity.restrictions[restriction] = entity.restrictions[restriction].restrictionValue
          ? entity.restrictions[restriction].restrictionValue.value
          : [];
      });
    }
    const group = entity.group && entity.group.replace("GROUP_", "");
    const groupNumber = group && parseInt(group);
    entity.group = groupNumber;
    return entity;
  });
}

/* TODO: There's a problem with the API, on UPDATE the id changes, is that normal?
 * Also when updating and the entity does not exists it returns a 200 instead of a 404 */
function PromotionsList({
  onDeleteListItem,
  onCloneListItem,
  onReorderListItem,
  entities,
  entity,
  config,
  locales,
  onToggleListItem,
  loading,
  entityError,
  entityResult,
  handleCreate,
  handleEdit,
  success,
  intl,
  history,
  hotelTicker,
  sendAPIRequest,
  actions,
}) {
  const [error, setError] = useState();
  let { filterFields } = config;
  const { name, entityName, validation, fields, renderers, sorting, allowClone, reorder, restConfig } = config;
  const chainData = useChainData();

  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const isChain = useMemo(() => chainData && chainData.isChain, [chainData]);

  const handleDelete = useCallback(
    ({ event, entity }) => {
      event.preventDefault();
      event.stopPropagation();
      return onDeleteListItem({
        id: entity.id,
        entity,
      });
    },
    [onDeleteListItem]
  );

  function handleClone(props) {
    onCloneListItem(props);
  }

  function handleReorder(props) {
    onReorderListItem(props);
  }

  async function handleToggle({ field, entity }) {
    const originalEntity = { ...entities.find(it => it.id === entity.id) };
    const isOldPromo = entity.sharingType === SHARING_TYPE.SIMPLE;
    if (isChain && !isOldPromo) {
      const type = field.fieldName === "isVisible" ? "visible" : "active";
      const url = `${restConfig.baseURL}/api/aggregator/${entity.propertyTicker}/engine/administration/promo/${entity.id}/update/${type}`;
      try {
        await sendAPIRequest({
          options: {
            url,
            method: "put",
            data: {
              enable: !originalEntity[field.fieldName],
            },
          },
          reloadEntities: true,
          ignoreState: false,
        });
        return history.push(`/${hotelTicker}/promotions/success`);
      } catch (error) {
        console.log(error);
        setError(error);
      }
    } else {
      delete originalEntity.id;
      delete originalEntity.propertyTicker;

      // Since the transform entity function expect the formValues format
      // instead of providing the originalEntity or the listEntity as base
      // we transform the originalEntity to formValues and THEN provide it
      // to the transform entity function so the update format is correct
      const functionMapper = customEntityToFormValuesMapper({ chainInventories: {} });
      const formValues = functionMapper({
        entity: originalEntity,
        fields: config.fields.form,
        locales: locales,
        translationConfig: config.translation,
      });
      const newEntity = transformEntity({ entity: { ...formValues } });
      newEntity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] =
        originalEntity.restrictions[RESTRICTION_TYPE.OPEN_BOOKING];
      onToggleListItem({
        id: entity.id,
        entity: newEntity,
        activeProperty: field.fieldName,
        requestObjectActiveProperty: field.fieldName,
      });
    }
  }

  const parsedEntities = bookableEntityToListValuesMapper({
    entities,
    currentLocale: config.locale,
  });
  if (
    Array.isArray(parsedEntities) &&
    parsedEntities.length > 0 &&
    !parsedEntities.some(it => it.sharingType && it.sharingType === SHARING_TYPE.SHARED)
  ) {
    const index = fields.list.findIndex(it => it.fieldName === "type");
    if (index > -1) {
      fields.list.splice(index, 1);
    }
    filterFields = filterFields.filter(it =>
      typeof it === "string" ? it !== "type" : it[0] !== "type"
    );
  }
  const displaySuccessAlert = useCallback(() => {
    swal(
      intl.formatMessage(messages.success),
      intl.formatMessage(messages.deleteSuccess),
      "success"
    );
  }, [intl]);

  const displayErrorAlert = useCallback(() => {
    swal(intl.formatMessage(messages.error), intl.formatMessage(messages.deleteError), "error");
  }, [intl]);

  const executeAlert = useCallback(
    async ({ event, entity, index }) => {
      let messageText = intl.formatMessage(messages.deleteConfirmationWarning);
      if (isChain) {
        messageText = `${intl.formatMessage(messages.confirmationIsChain1)} ${
          entity.ticker
        } ${intl.formatMessage(messages.confirmationIsChain2)}`;
      }
      swal({
        title: intl.formatMessage(messages.confirmation),
        text: messageText,
        icon: "warning",
        buttons: {
          cancel: intl.formatMessage(messages.cancel),
          confirm: {
            text: intl.formatMessage(messages.confirmDelete),
            closeModal: false,
            value: true,
          },
        },
        dangerMode: true,
      }).then(async willDelete => {
        if (willDelete) {
          const result = await handleDelete({ event, entity, index });
          if (
            result &&
            result.response &&
            (result.response.status !== 200 || result.response.status !== 201)
          ) {
            displayErrorAlert();
          } else {
            displaySuccessAlert();
          }
        }
        return false;
      });
    },
    [isChain, displayErrorAlert, displaySuccessAlert, handleDelete, intl]
  );

  const handleDownloadExcel = useCallback(async () => {
    setIsDownloadingExcel(true);
    try {
      const url = `${restConfig.baseURL}/api/backoffice/${entity.hotelTicker}/promotions/xlsx`;
      const response = await axios.get(url, { responseType: "blob" });
      const filename = response.headers["content-disposition"].split(";")[1].trim().split("=")[1];
      createExcelFileDownloadFromRawData(response.data, filename);
    } catch (e) {
      console.error(e);
    } finally {
      setIsDownloadingExcel(false);
    }
  }, [entity.hotelTicker, restConfig.baseURL]);

  return (
    <Container>
      <EntityList
        hasFilters
        filterFields={filterFields}
        controlsPosition={CONTROLS_POSITION.TOP}
        loading={loading}
        entityName={entityName}
        entities={parsedEntities}
        validation={validation}
        renderers={renderers}
        name={name}
        fields={fields}
        onToggle={handleToggle}
        onDelete={handleDelete}
        onClone={handleClone}
        entityError={entityError || error}
        entityResult={entityResult}
        handleCreate={handleCreate}
        handleEdit={handleEdit}
        customEntityToListValuesMapper={customEntityToListValuesMapper}
        currentLocale={config.locale}
        success={success}
        sorting={sorting}
        onSort={handleReorder}
        allowClone={allowClone}
        reorder={reorder}
        onReorder={handleReorder}
        maxColumnWidth={100}
        horizontalScroll
        allowDelete={false}
        isDownloadingExcel={isDownloadingExcel}
        onDownloadExcel={isChain && handleDownloadExcel}
        customActions={
          actions.allowDelete && [
            {
              classNames: "btn-danger",
              icon: "fa fa-trash-o",
              tooltipId: "delete-custom",
              tooltipText: intl.formatMessage(messages.deleteTooltip),
              onExecute: ({ event, entity, index }) => {
                executeAlert({ event, entity, index });
              },
              isAvailable: ({ event, entity, index }) => true,
            },
          ]
        }
      />
    </Container>
  );
}

const PromotionListWrapper = props => {
  const authorizer = useAuthorizer();
  const permissions = usePermissions();
  const section = ENTITY_NAME_TO_SECTION[props.entityName];
  const sectionPermissions = permissions.find(
    sectionPermissions => sectionPermissions.section === section
  );
  if (!sectionPermissions || !sectionPermissions.show) {
    return <ForbiddenSection showMessage={props.showForbiddenMessage} />;
  }
  const actionsPromos = {
    allowCreate: sectionPermissions.create,
    allowEdit: sectionPermissions.modify,
    allowDelete: authorizer.canExecute(actions.DeletePromo),
    allowImport: sectionPermissions.import,
    allowExport: sectionPermissions.export,
  };
  return <PromotionsList {...props} actions={actionsPromos} />;
};

PromotionsList.propTypes = {};

PromotionsList.defaultProps = {};

export default injectIntl(withPropertyData(PromotionListWrapper));
